import gql from 'graphql-tag';

import { executeGqlMutation } from 'src/graphql/client';
import { getSingleProp } from 'src/lib/graphql';

const MUTATION = gql`
  mutation CustomerResourcesFileAdd(
    $customerId: ID!
    $workspaceId: ID!
    $resourceFileType: CustomerResourceFileEnum!
    $name: String!
    $tags: [String!]!
    $fileInfo: CustomerResourcesFileAddInfo!
  ) {
    customerResourceFileAdd(
      customerId: $customerId
      workspaceId: $workspaceId
      resourceFileType: $resourceFileType
      name: $name
      tags: $tags
      fileInfo: $fileInfo
    ) {
      id
      type
      fileUploadInfo {
        fileUploadPresignedUrl
        fileUploadPresignedUrlExpiresAt
      }
    }
  }
`;

/**
 * Upload file info
 *
 * @typedef CustomerResourcesFileAddInfo
 * @type {object}
 * @property {string} name - file name
 * @property {number} size - file size
 * @property {string} [checksum] - file checksum
 * @property {object} [thumbnail] - image thumbnail
 */

/**
 * Response of the "customerResourceFileAdd" mutation
 *
 * @typedef CustomerResourcesFileAddResponse
 * @type {object}
 * @property {string} id - ID of the resource file
 * @property {string} uploadUrl - pre-signed URL for uploading the file
 */

/**
 * Create a new resource file for the current customer
 *
 * @param {ID} customerId - ID of the current customer
 * @param {ID} workspaceId - ID of the workspace
 * @param {string} resourceFileType - type of the resource file
 * @param {string} name - name of the file
 * @param {string[]} tags - tags
 * @param {CustomerResourcesFileAddInfo} fileInfo - file info
 * @returns {Promise<CustomerResourcesFileAddResponse>} - response of the mutation
 */
export const customerResourceFileAdd = async ({
  customerId,
  workspaceId,
  resourceFileType,
  name,
  tags,
  fileInfo,
}) => {
  const result = await executeGqlMutation({
    mutation: MUTATION,
    variables: { customerId, workspaceId, resourceFileType, name, tags, fileInfo },
  });

  const data = getSingleProp(result.data);
  if (!data) {
    return null;
  }

  return {
    id: data.id,
    uploadUrl: data.fileUploadInfo?.fileUploadPresignedUrl,
  };
};
